<template>
  <div class="wrn" data-test="wa-reminder-template-note">
    <template v-if="!loading">
      <template v-if="wording">
        <p class="wrn-lead" data-test="wa-reminder-template-lead">
          On WhatsApp, this message is sent instead of your text reminders and confirmation requests:
        </p>
        <blockquote class="wrn-wording" data-test="wa-reminder-template-wording">
          <WhatsAppMessageText :text="wording" />
        </blockquote>
        <p class="wrn-foot" data-test="wa-reminder-template-auto-reply">
          When a patient confirms on WhatsApp, your auto-reply template below is sent to them on WhatsApp.
        </p>
        <p class="wrn-foot" data-test="wa-reminder-template-see-account">See My Account &rarr; WhatsApp.</p>
      </template>
      <!-- No wording to show (the load failed, the viewer is not an Admin/Manager, or the message
           has no readable wording yet): still say what happens, without it. -->
      <p v-else class="wrn-lead" data-test="wa-reminder-template-fallback">
        On WhatsApp, your WhatsApp appointment reminder is sent instead of your text reminders and
        confirmation requests. When a patient confirms on WhatsApp, your auto-reply template below is
        sent to them on WhatsApp.
        See My Account &rarr; WhatsApp.
      </p>
    </template>
  </div>
</template>

<script>
/**
 * Appointment reminders page -> the note at a WhatsApp-only office.
 *
 * At such an office the text confirmations and reminders do not use the wiki text templates picked
 * below; teamio-utility sends the approved WhatsApp appointment reminder instead. This tells the
 * person on the page which message that is, so the template pickers don't mislead.
 *
 * AptReminderSettings.vue mounts this ONLY when the office is WhatsApp-only AND WhatsApp can deliver
 * appointment reminders (so the fetch never runs anywhere else). The wording comes from the shared
 * services/whatsappMessages.js - the same call as the My Account card. Only an APPROVED message's
 * wording is shown: a message still waiting for approval is not the one patients are getting.
 *
 * A failure (including a 403 for a viewer who is not an Admin/Manager) is not an error here: the
 * note simply says the same thing without the wording.
 */
import { fetchWhatsAppMessages } from '@/services/whatsappMessages';
import { findAppointmentReminder } from '@/utils/whatsappMessages';
import WhatsAppMessageText from '@/pages/MyAccount/components/WhatsAppMessageText.vue';

export default {
  name: 'WhatsAppReminderTemplateNote',

  components: { WhatsAppMessageText },

  props: {
    siteID: { type: String, default: '' },
  },

  data() {
    return {
      loading: true,
      wording: null,
    };
  },

  async mounted() {
    try {
      const templates = await fetchWhatsAppMessages(this.siteID, this.$store.state.getAuth());
      const reminder = findAppointmentReminder(templates);
      this.wording = reminder && reminder.status === 'approved' ? reminder.wording : null;
    } catch (e) {
      // Expected for a non-admin viewer (403). The fallback sentence covers it.
      this.wording = null;
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style lang="scss" scoped>
@import '../../MyAccount/components/PatientPayments/tokens';

.wrn {
  /* The Text Confirmations header floats a switch to its left; start below it. */
  clear: both;
}

.wrn-lead,
.wrn-foot {
  margin: 0.6rem 0 0;
  color: $pp-text-muted;
  font-size: 0.88rem;
  line-height: 1.6;
}

.wrn-wording {
  margin: 0.5rem 0 0;
  padding: 0.6rem 0.8rem;
  border: 1px solid $pp-border;
  border-radius: $pp-radius-sm;
  background: $pp-surface-sunken;
  color: $pp-text-strong;
  font-size: 0.88rem;
  line-height: 1.6;
  overflow-wrap: anywhere;
}
</style>
